import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Home, Loader2, AlertTriangle, Share2, Printer, Flag, ArrowLeft, Info } from 'lucide-react';
import api from '@/lib/axios';
import MarketplaceFooter from '@/components/marketplace/MarketplaceFooter';
import { TipoBadge } from '@/pages/NewsCenterPage';
import { editorialAdminUrl, editorialArticleUrl, editorialDate, useEditorialPreview } from '@/hooks/useEditorialPreview';
import '@/pages/news-content.css';

const REPORT_EMAIL = 'geral@marketplacetvde.pt';

export default function NewsArticlePage({ preview = false }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [rel, setRel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('ok'); // ok | notfound | error
  useEditorialPreview(preview);

  useEffect(() => {
    let active = true;
    setLoading(true); setStatus('ok'); setArticle(null); setRel([]);
    api.get(preview ? `/admin/marketplace/news/preview/${slug}` : `/marketplace/news/articles/${slug}`)
      .then((r) => {
        if (!active) return;
        if (r.data.redirect) { navigate(`/noticias-tvde/artigo/${r.data.redirect}`, { replace: true }); return; }
        setArticle(r.data.article); setRel(r.data.relacionados || []);
        document.title = `${(r.data.article.seo?.title) || r.data.article.titulo} | Marketplace TVDE`;
      })
      .catch((e) => { if (active) setStatus([401, 403].includes(e.response?.status) ? 'unauthorized' : e.response?.status === 404 ? 'notfound' : 'error'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [slug, navigate, preview]);

  const share = async () => {
    const url = window.location.href;
    try { if (navigator.share) { await navigator.share({ title: article.titulo, url }); return; } } catch (e) { /* noop */ }
    try { await navigator.clipboard.writeText(url); } catch (e) { /* noop */ }
  };

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-slate-50 text-slate-500" data-testid="article-loading"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> A carregar…</div>;

  if (status === 'unauthorized') return <Shell preview={preview}><div role="alert" data-testid="article-auth-required" className="p-6 text-amber-900">Sessão de administrador necessária. <Link to="/login" data-testid="article-login-link" className="underline">Entrar</Link></div></Shell>;

  if (status === 'notfound') return (
    <Shell preview={preview}><div className="rounded-2xl border border-slate-200 bg-white p-10 text-center" data-testid="article-notfound">
      <p className="text-lg font-bold text-slate-900">Conteúdo não encontrado</p>
      <p className="mt-1 text-sm text-slate-500">Este artigo pode ter sido retirado ou nunca ter existido.</p>
      <Link to={preview ? editorialAdminUrl : '/noticias-tvde'} data-testid="article-notfound-back" className="mt-4 inline-block rounded-full bg-emerald-600 px-5 py-2 text-sm font-bold text-white">Voltar</Link>
    </div></Shell>
  );

  if (status === 'error') return (
    <Shell preview={preview}><div className="rounded-2xl border border-amber-200 bg-amber-50 p-10 text-center" data-testid="article-error">
      <AlertTriangle className="mx-auto h-8 w-8 text-amber-500" />
      <p className="mt-3 font-bold text-amber-900">Não foi possível carregar o artigo. Problema temporário de ligação.</p>
      <button data-testid="article-retry" onClick={() => window.location.reload()} className="mt-4 rounded-full bg-amber-500 px-5 py-2 text-sm font-bold text-white">Tentar novamente</button>
    </div></Shell>
  );

  const g = article.guia_seccoes || {};
  const isGuide = article.tipo === 'guia';

  return (
    <Shell preview={preview}>
      {preview && <div className="mb-4 border border-amber-200 bg-amber-50 p-3 text-sm font-semibold text-amber-900" data-testid="article-preview-banner">Pré-visualização autenticada · Não altera o estado de publicação</div>}
      <nav className="mb-4 flex flex-wrap items-center gap-1 text-xs text-slate-500" data-testid="article-breadcrumbs">
        <Link to={preview ? editorialAdminUrl : '/noticias-tvde'} data-testid="article-center-link" className="hover:text-emerald-700">Centro de Informação</Link>
        {article.categoria && <><span>/</span>{preview ? <span data-testid="article-category">{article.categoria.replace(/-/g, ' ')}</span> : <Link data-testid="article-category-link" to={`/noticias-tvde/categoria/${article.categoria}`} className="hover:text-emerald-700">{article.categoria.replace(/-/g, ' ')}</Link>}</>}
      </nav>

      <article className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8" data-testid="article-body">
        <div className="flex flex-wrap items-center gap-2"><TipoBadge tipo={article.tipo} /></div>
        <h1 data-testid="article-title" className="mt-3 break-words text-3xl font-extrabold leading-tight text-slate-900 sm:text-4xl">{article.titulo}</h1>
        {article.resumo && <p data-testid="article-summary" className="mt-3 text-lg text-slate-600">{article.resumo}</p>}

        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
          <span data-testid="article-author">Por <strong className="text-slate-700">{article.autor}</strong></span>
          {article.data_publicacao && <span data-testid="article-publication-date">Publicado: {editorialDate(article.data_publicacao)}</span>}
          {article.data_ultima_revisao && <span data-testid="article-review-date">Última revisão: {editorialDate(article.data_ultima_revisao)}</span>}
          {article.territorio && <span data-testid="article-territory">Território: {article.territorio}</span>}
        </div>

        {article.tipo === 'comunicado' && (
          <div className="mt-4 flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700" data-testid="article-comunicado-note">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
            <span>Este é um comunicado de uma entidade externa. Não constitui legislação nem a posição do Marketplace TVDE.</span>
          </div>
        )}
        {article.revisao_juridica_pendente && (
          <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm font-semibold text-amber-800" data-testid="article-review-pending">
            Conteúdo com revisão jurídica/fiscal pendente.
          </div>
        )}

        {article.imagem?.url && (
          <figure className="mt-6">
            <img src={article.imagem.url} alt={article.imagem.alt || article.titulo} className="w-full rounded-xl border border-slate-200" />
            {(article.imagem.credito || article.imagem.licenca) && <figcaption className="mt-1 text-[11px] text-slate-400">{article.imagem.credito} {article.imagem.licenca ? `· ${article.imagem.licenca}` : ''}</figcaption>}
          </figure>
        )}

        {isGuide && (g.em_poucas_palavras || g.a_quem_se_aplica || g.o_que_fazer || g.exemplo_pratico || g.excecoes) && (
          <div className="mt-6 space-y-3 rounded-xl border border-sky-100 bg-sky-50/60 p-4" data-testid="article-guide-boxes">
            {g.em_poucas_palavras && <GuideBox t="Em poucas palavras" v={g.em_poucas_palavras} />}
            {g.a_quem_se_aplica && <GuideBox t="A quem se aplica" v={g.a_quem_se_aplica} />}
            {g.o_que_fazer && <GuideBox t="O que fazer" v={g.o_que_fazer} />}
            {g.exemplo_pratico && <GuideBox t="Exemplo prático" v={g.exemplo_pratico} />}
            {g.excecoes && <GuideBox t="Exceções" v={g.excecoes} />}
          </div>
        )}

        <div className="news-content mt-6 max-w-none text-slate-800" data-testid="article-content" dangerouslySetInnerHTML={{ __html: article.conteudo_html || '' }} />

        {(article.fontes || []).length > 0 && (
          <section className="mt-8 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="article-sources">
            <h2 className="text-sm font-extrabold text-slate-900">Fontes</h2>
            <ul className="mt-2 space-y-1 text-sm text-slate-600">
              {article.fontes.map((f, i) => (
                <li key={i}>
                  <span className="font-semibold text-slate-700">{f.entidade}</span>{f.titulo ? ` — ${f.titulo}` : ''}
                  {f.ligacao && <> · <a data-testid={`article-source-link-${i}`} href={f.ligacao} target="_blank" rel="noopener noreferrer nofollow" className="text-emerald-700 underline">ligação</a></>}
                  {f.data_consulta && <span data-testid={`article-source-date-${i}`} className="text-slate-400"> (consultado {editorialDate(f.data_consulta)})</span>}
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="mt-8 flex flex-wrap gap-2 border-t border-slate-100 pt-5">
          {!preview && <button onClick={share} className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:border-emerald-300" data-testid="article-share"><Share2 className="h-4 w-4" /> Partilhar</button>}
          <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:border-emerald-300" data-testid="article-print"><Printer className="h-4 w-4" /> Imprimir</button>
          {!preview && <a href={`mailto:${REPORT_EMAIL}?subject=${encodeURIComponent('Incorreção no artigo: ' + article.titulo)}`} className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:border-amber-300" data-testid="article-report"><Flag className="h-4 w-4" /> Comunicar incorreção</a>}
        </div>
      </article>

      {rel.length > 0 && (
        <section className="mt-8" data-testid="article-related">
          <h2 className="text-lg font-extrabold text-slate-900">Conteúdos relacionados</h2>
          <div className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {rel.map((r) => (
              <Link key={r.slug} data-testid={`article-related-${r.slug}`} to={preview ? editorialArticleUrl(r.slug) : `/noticias-tvde/artigo/${r.slug}`} className="min-w-0 break-words rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-emerald-300">
                <TipoBadge tipo={r.tipo} testId={`article-related-type-${r.slug}`} />
                <h3 className="mt-2 font-bold text-slate-900">{r.titulo}</h3>
                {r.resumo && <p className="mt-1 line-clamp-2 text-sm text-slate-600">{r.resumo}</p>}
              </Link>
            ))}
          </div>
        </section>
      )}
    </Shell>
  );
}

function GuideBox({ t, v }) {
  return <div><p className="text-xs font-bold uppercase tracking-wide text-sky-700">{t}</p><p className="text-sm text-slate-700">{v}</p></div>;
}

function Shell({ children, preview }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white print:hidden">
        <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-3 px-4 py-4">
          <Link to="/" data-testid="article-marketplace-link" className="inline-flex items-center gap-2 text-sm font-bold text-slate-700 hover:text-emerald-700"><Home className="h-4 w-4" /> Marketplace TVDE</Link>
          <Link to={preview ? editorialAdminUrl : '/noticias-tvde'} data-testid="article-back-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700"><ArrowLeft className="h-4 w-4" /> {preview ? 'Voltar ao CMS' : 'Centro de Informação'}</Link>
        </div>
      </header>
      <main className="mx-auto max-w-4xl break-words px-4 py-8">{children}</main>
      {!preview && <div className="print:hidden"><MarketplaceFooter /></div>}
    </div>
  );
}
